
import React from "react";
import { cn } from "@/lib/utils";
import { Quote, Star } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";

interface TestimonialCardProps {
  quote: string;
  author: string;
  role?: string;
  className?: string;
  imageUrl?: string;
  rating?: number;
}

export function TestimonialCard({
  quote,
  author,
  role,
  className,
  imageUrl,
  rating
}: TestimonialCardProps) {
  // Use initials for fallback avatar
  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return (
    <Card
      className={cn(
        "bg-white rounded-xl shadow-lg h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-none",
        className
      )}
    >
      <CardContent className="p-8 flex flex-col h-full">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="h-16 w-16 border-2 border-grandpal-green-dark">
            <AvatarImage
              src={imageUrl || "/placeholder.svg"}
              alt={author}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/placeholder.svg';
              }}
              className="object-cover"
            />
            <AvatarFallback className="bg-grandpal-green-lighter text-grandpal-green-dark font-bold text-xl">
              {getInitials(author)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h4 className="font-medium text-lg text-grandpal-navy">{author}</h4>
            {role && <p className="text-sm text-grandpal-gray">{role}</p>}
            {rating && (
              <div className="flex items-center mt-1">
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={cn(
                        "fill-current",
                        i < rating
                          ? "text-yellow-400"
                          : "text-gray-300"
                      )}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
        
        <Quote className="h-8 w-8 text-grandpal-green-dark opacity-30 mb-4" />
        
        <p className="text-grandpal-navy text-lg flex-grow">"{quote}"</p>
      </CardContent>
    </Card>
  );
}
